import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, AlertCircle, CheckCircle2, CircleHelp, Thermometer, Info, ExternalLink, ChevronDown } from "lucide-react";
import { VitalsModal } from "@/components/PatientDashboardV2/components/VitalsModal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";

import {
  getPathway,
  getPgd,
  getDrug,
  resolveDoseBand,
  calculateDose,
  validateAllConfigs,
  createAuditRecord,
  recordBannerConfirmation,
  recordAnswer,
  recordGateway,
  recordDrugSelection,
  recordPgdExclusionAnswer,
  recordOutcome,
  recordSupplyDetails,
  buildPrefillItemsFromPgd,
  buildSupplyDetailsFromPgd,
  buildAllowedMedicationsFromPgd,
} from "@/lib/pharmacyFirst";
import { sharedLeaflets } from "@/lib/pharmacyFirst/leaflets";
import { PrescriptionModal } from "@/components/PrescriptionModal";
import { matchesDrugSelectionRule } from "@/lib/pharmacyFirst/engine/drugSelection";
import {
  getPgdExclusionCheckPgdId,
  shouldSelectOverridePgd,
} from "@/lib/pharmacyFirst/engine/pgdExclusion";
import { resolveEliminationStep, computeEliminationAnswer } from "@/lib/pharmacyFirst/engine/eliminationRouting";
import { resolveConditionRoute } from "@/lib/pharmacyFirst/engine/conditionRouting";
import { getIOTFTable } from "@/lib/pharmacyFirst/pgds/aht/iotf-registry";

import type {
  PatientInput,
  AuditRecord,
  Pathway,
  PathwayNode,
  OutcomeRef,
  Amendment,
  ChecklistNode as ChecklistNodeShape,
  ConditionRouteContext,
  EliminationLogEntry,
} from "@/lib/pharmacyFirst";

import { DemographicConfirmationBanner } from "./DemographicConfirmationBanner";
import {
  YesNoNode,
  ChecklistNode,
  SingleSelectNode,
  MultiSelectNode,
  SeverityNode,
  ScoreNode,
  GatewayAlarmNode,
  GatewayProgressionNode,
  WeightCaptureNode,
  OutcomeNode,
  DrugSelectionNode,
  DrugEliminationNode,
  DrugResolutionNode,
  NumericInputNode,
  ClinicianAssessmentNode,
  AttestationNode,
  IOTFLookupNode,
  ConfirmationCardNode,
  BMICaptureNode,
  InlineBPCaptureNode,
} from "./nodes";
import type { DrugEliminationAnswer } from "./nodes";
import type { DrugResolutionAnswer } from "./nodes";
import type { NumericInputAnswer } from "./nodes";
import type { ClinicianAssessmentAnswer } from "./nodes";
import type { AttestationAnswer } from "./nodes";
import type { IOTFLookupAnswer } from "./nodes";
import type { ConfirmationCardResult } from "./nodes";
import type { BMICaptureAnswer } from "./nodes";
import { ConsultationSummary } from "./ConsultationSummary";
import { AHTTreatmentPlanConfirmModal } from "./AHTTreatmentPlanConfirmModal";
import { buildAHTTreatmentPlan } from "@/lib/pharmacyFirst/utils/buildAHTTreatmentPlan";
import { generateAHTAppointments } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";
import { calculateTreatmentPlanDates } from "@/lib/pharmacyFirst/utils/calculateTreatmentPlanDates";
import type { AHTWeightTreatmentPlan, AHTDrugId } from "@/lib/ahtTreatmentPlanData";
import type { AHTPlannedAppointment, GenerateAHTAppointmentsInput } from "@/lib/pharmacyFirst/utils/generateAHTAppointments";
import { usePathwayAutoSave } from "@/lib/pharmacyFirst/saveResume";
import type { PathwayEngineSnapshot, AhtChainSnapshot } from "@/lib/pharmacyFirst/saveResume";

interface PathwayEngineProps {
  pathwayId: string;
  patient: PatientInput;
  isDarkMode?: boolean;
  initialContextValues?: Record<string, unknown>;
  onComplete: (auditRecord: AuditRecord) => void;
  onCancel?: () => void;
  /**
   * Called when an outcome links to a calculator (e.g. NEWS2 from emergency).
   * The host opens the calculator in the actions column.
   */
  onOpenCalculator?: (calculatorId: string) => void;
  /**
   * Called when the pharmacist captures a vital reading from inside a PGD
   * pre-supply check (e.g. recording a temperature for the temp >39°C check).
   */
  onSaveVitals?: (vital: any) => void;
  onConsentRefused?: () => void;
  onCreateAHTTreatmentPlan?: (plan: AHTWeightTreatmentPlan) => void | Promise<void>;
  onDemographicAmended?: (amendments: Amendment[]) => void;
  patientDisplayName?: string;
  patientEmail?: string | null;
  onUpdatePatientEmail?: (email: string) => Promise<void>;
  patientVitals?: { height?: { value?: number | string }; weight?: { value?: number | string }; bloodPressure?: { systolic?: { value?: number | string }; diastolic?: { value?: number | string } } } | null;
  restoredState?: PathwayEngineSnapshot;
  consultationId?: string;
  companyId?: string;
  templateName?: string;
  hasExistingTemplate?: boolean;
  templateId?: string;
  chainState?: AhtChainSnapshot;
}

type EngineState = "banner" | "consulting" | "prescribing" | "summary" | "complete";

/** Whether a drug_selection node renders an interactive UI (vs auto-advancing) */
function isInteractiveDrugSelection(node: any): boolean {
  return node?.type === "drug_selection" &&
    (node?.variant === "recommend_with_override" || node?.variant === "equal_choice");
}

/**
 * Evaluate a yes_no autoAnswer rule against the current patient.
 * Returns true when every supplied condition has a definite match (AND logic).
 * Supports conditions that can be used together to auto-answer a question.
 * Any unknown patient field (e.g. sex unknown, age unknown) means the
 * question is left for the pharmacist instead of being auto-answered.
 */
function evalAutoAnswer(rule: any, patient: PatientInput): boolean {
  if (!rule?.when) return false;
  const w = rule.when;

  // Check patientIsFemale condition
  if (w.patientIsFemale !== undefined) {
    if (patient.isFemale === undefined) return false;
    if (patient.isFemale !== w.patientIsFemale) return false;
  }

  // Check patientAgeAtLeast condition
  if (w.patientAgeAtLeast !== undefined) {
    if (patient.ageYears === undefined || patient.ageYears === null) return false;
    if (!(patient.ageYears >= w.patientAgeAtLeast)) return false;
  }

  // Check patientAgeUnder condition
  if (w.patientAgeUnder !== undefined) {
    if (patient.ageYears === undefined || patient.ageYears === null) return false;
    if (!(patient.ageYears < w.patientAgeUnder)) return false;
  }

  // Check hasMedication condition — case-insensitive match against currentMedications.
  // Fires when ANY keyword in hasMedication matches ANY entry in currentMedications.
  if (w.hasMedication !== undefined) {
    if (!patient.currentMedications || patient.currentMedications.length === 0) return false;
    const meds = patient.currentMedications.map((m) => m.toLowerCase());
    const matched = w.hasMedication.some((keyword: string) => {
      const kw = keyword.toLowerCase();
      return meds.some((med) => med.includes(kw));
    });
    if (!matched) return false;
  }

  // All conditions matched
  return true;
}

export function PathwayEngine({
  pathwayId,
  patient,
  isDarkMode = false,
  initialContextValues,
  onComplete,
  onCancel,
  onOpenCalculator,
  onSaveVitals,
  onConsentRefused,
  onCreateAHTTreatmentPlan,
  onDemographicAmended,
  patientDisplayName,
  patientEmail,
  onUpdatePatientEmail,
  patientVitals,
  restoredState,
  consultationId,
  companyId,
  templateName,
  hasExistingTemplate = false,
  templateId,
  chainState,
}: PathwayEngineProps) {
  // Config validation on mount
  useEffect(() => {
    try {
      validateAllConfigs();
    } catch (error) {
      console.error("Config validation failed:", error);
      // In production, this would show an error screen
    }
  }, []);

  const pathway = useMemo(() => {
    try {
      const p = getPathway(pathwayId);
      if (!p) return null;
      const linksMap = (p.links || {});
      // Resolve any linkRefs on nodes/outcomes into clinicalLinks so components
      // can continue to read node.clinicalLinks without changes.
      const nodes = (p.nodes || []).map((n: any) => {
        const nn = { ...n };
        if ((!(Array.isArray(nn.clinicalLinks)) || nn.clinicalLinks.length === 0) && Array.isArray(nn.linkRefs)) {
          nn.clinicalLinks = nn.linkRefs.map((r: string) => linksMap[r]).filter(Boolean);
        }
        return nn;
      });
      const outcomes = Object.fromEntries(
        Object.entries(p.outcomes || {}).map(([k, o]: any) => {
          const oo = { ...o };
          if ((!(Array.isArray(oo.clinicalLinks)) || oo.clinicalLinks.length === 0) && Array.isArray(oo.linkRefs)) {
            oo.clinicalLinks = oo.linkRefs.map((r: string) => linksMap[r]).filter(Boolean);
          }
          return [k, oo];
        }),
      );

      const merged = {
        ...p,
        nodes,
        outcomes,
        leaflets: {
          ...sharedLeaflets,
          ...(p.leaflets || {}),
        },
      } as unknown as Pathway;
      return merged;
    } catch (error) {
      console.error("Failed to load pathway:", error);
      return null;
    }
  }, [pathwayId]);

  // Engine state — skip banner for chained pathways with no demographic fields
  const [engineState, setEngineState] = useState<EngineState>(() => {
    if (restoredState) return restoredState.engineState;
    const hasBannerFields = pathway?.demographicConfiguration?.bannerFields?.length > 0;
    return hasBannerFields ? "banner" : "consulting";
  });
  const [audit, setAudit] = useState<AuditRecord>(() =>
    restoredState?.audit ?? createAuditRecord(pathwayId, patient)
  );
  const [currentNodeId, setCurrentNodeId] = useState<string>(() => {
    if (restoredState?.currentNodeId) {
      const nodeExists = pathway?.nodes?.some((n: any) => n.id === restoredState.currentNodeId);
      const outcomeExists = restoredState.currentNodeId in (pathway?.outcomes ?? {});
      if (nodeExists || outcomeExists) return restoredState.currentNodeId;
    }
    return pathway?.entryNode || "";
  });
  const [answers, setAnswers] = useState<Map<string, unknown>>(() =>
    restoredState?.answers ? new Map(Object.entries(restoredState.answers)) : new Map()
  );
  const [contextValues, setContextValues] = useState<Record<string, unknown>>(
    restoredState?.contextValues ?? initialContextValues ?? {}
  );
  const [history, setHistory] = useState<string[]>(restoredState?.history ?? []);
  const [selectedPgdId, setSelectedPgdId] = useState<string | null>(restoredState?.selectedPgdId ?? null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [demographicBlockMessage, setDemographicBlockMessage] = useState<string | null>(null);
  const [exclusionAnswers, setExclusionAnswers] = useState<
    Record<string, "yes" | "no">
  >(restoredState?.exclusionAnswers ?? {});
  const [prefilledExclusionIds, setPrefilledExclusionIds] = useState<Set<string>>(
    new Set(),
  );
  const [capturedWeightKg, setCapturedWeightKg] = useState<number | null>(restoredState?.capturedWeightKg ?? null);
  const [clinicalRefsOpen, setClinicalRefsOpen] = useState(false);

  const [eligibleDrugIds, setEligibleDrugIds] = useState<string[]>(
    () => restoredState?.eligibleDrugIds ?? pathway?.initialEligibleDrugIds ?? [],
  );
  const [eliminationLog, setEliminationLog] = useState<EliminationLogEntry[]>(restoredState?.eliminationLog ?? []);
  const [resolvedDrugId, setResolvedDrugId] = useState<string | null>(restoredState?.resolvedDrugId ?? null);
  const [accumulatedFlags, setAccumulatedFlags] = useState<string[]>(restoredState?.accumulatedFlags ?? []);
  const [managementPlanCaptures, setManagementPlanCaptures] = useState<
    { nodeId: string; planText: string; patientAcknowledged: boolean; capturedAt: string }[]
  >(restoredState?.managementPlanCaptures ?? []);
  const [preferenceCaptures, setPreferenceCaptures] = useState<
    { nodeId: string; key: string; value: unknown; capturedAt: string }[]
  >(restoredState?.preferenceCaptures ?? []);

  const [showAHTTreatmentPlanModal, setShowAHTTreatmentPlanModal] = useState(false);
  const [ahtPlannedAppointments, setAhtPlannedAppointments] = useState<AHTPlannedAppointment[]>([]);
  const [ahtTreatmentPlanCreated, setAhtTreatmentPlanCreated] = useState(false);

  usePathwayAutoSave({
    consultationId: consultationId ?? "",
    companyId: companyId ?? "",
    templateName: templateName ?? "",
    hasExistingTemplate: hasExistingTemplate,
    templateId,
    pathwayId,
    engineState,
    currentNodeId,
    history,
    answers,
    contextValues,
    audit,
    selectedPgdId,
    exclusionAnswers,
    capturedWeightKg,
    eligibleDrugIds,
    eliminationLog,
    resolvedDrugId,
    accumulatedFlags,
    managementPlanCaptures,
    preferenceCaptures,
    chainState,
  });

  /** Patient with mid-flow captures overlaid (e.g. weight). */
  const effectivePatient: PatientInput = useMemo(
    () =>
      capturedWeightKg !== null && capturedWeightKg !== undefined
        ? { ...patient, weightKg: capturedWeightKg }
        : patient,
    [patient, capturedWeightKg],
  );

  const activeDoseStepId = contextValues["doseStepId"] as string | undefined;

  const handleCreateAHTTreatmentPlan = useCallback(() => {
    if (!pathway.id.startsWith("aht-")) return;
    const drugId = audit.supplyDetails?.drugId;
    const pgdId = audit.supplyDetails?.pgdId ?? selectedPgdId;
    if (!drugId || !pgdId) return;

    let pgd: ReturnType<typeof getPgd> | undefined;
    try { pgd = getPgd(pgdId); } catch { return; }
    const reviewSchedule = pgd?.reviewSchedule;
    if (!reviewSchedule) return;

    const today = new Date().toISOString().split("T")[0] ?? new Date().toISOString().substring(0, 10);
    const appointments = generateAHTAppointments({
      initialSupplyDate: today,
      drugId: drugId as GenerateAHTAppointmentsInput["drugId"],
      sectionBReviewDays: reviewSchedule.sectionBReviewDays,
      drugSpecificReviewDays: reviewSchedule.drugSpecificReviewDays,
      drugSpecificReviewLabel: reviewSchedule.drugSpecificReviewLabel,
    });
    setAhtPlannedAppointments(appointments);
    setShowAHTTreatmentPlanModal(true);
  }, [audit.supplyDetails, selectedPgdId, pathway.id]);

  // Memoize prefill items so identity is stable while the same PGD is selected.
  const prefillItems = useMemo(() => {
    if (!selectedPgdId) return [];
    try {
      return buildPrefillItemsFromPgd(selectedPgdId, effectivePatient, activeDoseStepId);
    } catch (e) {
      console.error("Failed to build prefill items:", e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPgdId, patient.patientId, capturedWeightKg, activeDoseStepId]);

  const allowedMedications = useMemo(() => {
    if (!selectedPgdId) return [];
    try {
      return buildAllowedMedicationsFromPgd(selectedPgdId, effectivePatient, activeDoseStepId);
    } catch (e) {
      console.error("Failed to build allowed medications:", e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPgdId, patient.patientId, capturedWeightKg, activeDoseStepId]);

  /**
   * Returns true when a weight_capture node should auto-advance (i.e. weight
   * is not actually needed for the selected PGD, or the patient already has
   * a recorded weight).
   */
  const weightCaptureSatisfied = (): boolean => {
    if (!selectedPgdId) return true;
    try {
      const pgd = getPgd(selectedPgdId);
      if (!pgd.requiresWeightInput) return true;
      const age = effectivePatient.ageYears;
      if (
        age === undefined ||
        age === null ||
        age < pgd.requiresWeightInput.min ||
        age > pgd.requiresWeightInput.max
      ) {
        return true;
      }
      return typeof effectivePatient.weightKg === "number";
    } catch {
      return true;
    }
  };

  const buildConditionRouteCtx = (): ConditionRouteContext => ({
    patient: effectivePatient,
    context: contextValues,
    eligibleDrugIds,
  });

  const isAutoAdvanceNode = (id: string) => {
    if (!pathway) return false;
    const n = pathway.nodes.find((x) => x.id === id) as any;
    if (!n) return false;
    if (n.type === "flag") return n.autoAdvance !== false;
    if (n.type === "condition_route") return true;
    if (n.type === "drug_selection") return !isInteractiveDrugSelection(n);
    if (n.type === "pgd_exclusion_check") {
      const pgdId = n.pgdIdOverride || selectedPgdId;
      if (pgdId) {
        try { return (getPgd(pgdId).preSupplyChecks || []).length === 0; } catch { /* fall through */ }
      }
    }
    if (n.type === "weight_capture") return weightCaptureSatisfied();
    if (n.type === "yes_no") {
      if (evalAutoAnswer(n.autoAnswer, patient)) {
        return true;
      }
      if (n.id === "q13_pregnant_under16_guard" && patient.isFemale === true && patient.ageYears !== undefined && patient.ageYears >= 16) {
        return true;
      }
    }
    if (n.type === "drug_elimination" && evalAutoAnswer(n.autoAnswer, patient)) {
      return true;
    }
    return false;
  };

  const handleStepBack = () => {
    if (!pathway || history.length === 0) {
      onCancel?.();
      return;
    }
    const newHistory = [...history];
    let target = newHistory.pop() as string;
    while (newHistory.length > 0 && isAutoAdvanceNode(target)) {
      target = newHistory.pop() as string;
    }
    // If we ended up on an auto-advance node with no further history, there
    // is nothing visible to step back to — treat as cancel.
    if (isAutoAdvanceNode(target) && newHistory.length === 0) {
      onCancel?.();
      return;
    }

    // Preserve answers for nodes we're returning to and all nodes before
    const nodesToKeep = new Set([...newHistory, target]);
    const newAnswers = new Map<string, unknown>();
    for (const [id, value] of answers) {
      if (nodesToKeep.has(id)) {
        newAnswers.set(id, value);
      }
    }
    setAnswers(newAnswers);
    setHistory(newHistory);

    // Prune audit trail: remove gateways from abandoned branches only
    setAudit((prev) => {
      const remainingGateways = prev.gatewaysReached.filter((g) =>
        newHistory.includes((g as any).gatewayId) || (g as any).gatewayId === target
      );
      // Re-derive payment from gateways that are still on the kept path
      const paymentStillTriggered = remainingGateways.some((g) => {
        const node = pathway?.nodes.find((n) => n.id === (g as any).gatewayId) as any;
        return node?.triggersPayment !== false;
      });
      return {
        ...prev,
        gatewaysReached: remainingGateways,
        paymentTriggered: paymentStillTriggered,
        paymentTriggeredAt: paymentStillTriggered ? prev.paymentTriggeredAt : undefined,
        paymentTriggeredBy: paymentStillTriggered ? prev.paymentTriggeredBy : undefined,
      };
    });

    setErrorMessage(null);
    setCurrentNodeId(target);
  };

  // Auto-advance for nodes that have no UI (drug_selection auto, flag),
  // and for yes_no nodes whose autoAnswer rule is satisfied by patient context
  // (e.g. skip "is the patient pregnant?" for a known-male patient).
  useEffect(() => {
    if (engineState !== "consulting" || !pathway) return;
    const node =
      pathway.nodes.find((n) => n.id === currentNodeId) ||
      pathway.outcomes[currentNodeId];
    if (!node) return;
    if (node.type === "flag" && (node as any).autoAdvance !== false) {
      handleAnswer(null);
      return;
    }
    if (node.type === "drug_selection" && !isInteractiveDrugSelection(node)) {
      handleAnswer(null);
      return;
    }
    if (node.type === "weight_capture" && weightCaptureSatisfied()) {
      handleAnswer(null);
      return;
    }
    if (node.type === "condition_route") {
      handleAnswer(null);
      return;
    }
    if (node.type === "pgd_exclusion_check") {
      const pgdId = (node as any).pgdIdOverride || selectedPgdId;
      if (pgdId) {
        try {
          const pgd = getPgd(pgdId);
          if ((pgd.preSupplyChecks || []).length === 0) {
            handleAnswer({});
            return;
          }
        } catch { /* pgd not found — let the node render normally */ }
      }
    }
    if (node.type === "yes_no") {
      const n = node as any;
      // Only auto-answer when patient context yields a definite match.
      // If any required field is unknown, leave the question for the pharmacist.
      if (evalAutoAnswer(n.autoAnswer, patient)) {
        handleAnswer(n.autoAnswer.value ? "yes" : "no");
        return; // Prevent further renders of this node
      }
      // Additional auto-answer for q13: females aged 16+
      else if (n.id === "q13_pregnant_under16_guard" && patient.isFemale === true && patient.ageYears !== undefined && patient.ageYears >= 16) {
        handleAnswer(false); // Auto answer "No"
      }
    }
    if (node.type === "drug_elimination") {
      const n = node as any;
      if (evalAutoAnswer(n.autoAnswer, patient)) {
        const autoVal = n.autoAnswer.value as boolean;
        const { eliminatedDrugIds, logEntries } = computeEliminationAnswer({
          node: n,
          answer: autoVal,
          eligibleDrugIds,
          patient: effectivePatient,
          context: contextValues,
        });
        handleAnswer({
          answer: autoVal,
          eliminatedDrugIds,
          logEntries,
          managementPlan: null,
        } as DrugEliminationAnswer);
        return;
      }
    }
  }, [currentNodeId, engineState]);

  // Record gateway passage when entering a gateway node
  useEffect(() => {
    if (!pathway) return;
    const node = pathway.nodes.find((n) => n.id === currentNodeId);
    if (!node || (node.type !== "gateway_alarm" && node.type !== "gateway_progression")) {
      return;
    }
    // Mark gateway as reached and record payment if triggered
    const n = node as any;
    setAudit((prev) =>
      recordGateway(
        prev,
        currentNodeId,
        node.type === "gateway_alarm" ? "alarm" : "progression",
        n.triggersPayment !== false,
      ),
    );
  }, [currentNodeId]);

  // Pre-fill exclusion answers when entering a pgd_exclusion node
  useEffect(() => {
    if (!pathway) return;
    const node = pathway.nodes.find((n) => n.id === currentNodeId);
    if (!node || node.type !== "pgd_exclusion_check") {
      setExclusionAnswers({});
      setPrefilledExclusionIds(new Set());
      return;
    }
    const pgdId = getPgdExclusionCheckPgdId(node, selectedPgdId);
    if (!pgdId) {
      setExclusionAnswers({});
      setPrefilledExclusionIds(new Set());
      return;
    }
    let pgd;
    try {
      pgd = getPgd(pgdId);
    } catch {
      setExclusionAnswers({});
      setPrefilledExclusionIds(new Set());
      return;
    }
    const prefillMap = pathway.exclusionPrefillMap || {};
    const next: Record<string, "yes" | "no"> = {};
    const prefilled = new Set<string>();

    // First, restore any previously answered exclusion questions
    const prevAnswers = answers.get(currentNodeId);
    if (prevAnswers && typeof prevAnswers === "object") {
      Object.assign(next, prevAnswers);
    }

    for (const check of pgd.preSupplyChecks) {
      // Skip if already answered (e.g., from going back)
      if (next[check.id]) continue;

      // Universal consent prefill: if pharmacist confirmed consent at the
      // demographic banner stage, auto-answer "consent refused?" with No.
      if (
        contextValues.consentObtained === true &&
        /consent/i.test(check.id + " " + (check.question || ""))
      ) {
        next[check.id] = "no";
        prefilled.add(check.id);
        continue;
      }
      const rule = prefillMap[check.id];
      if (!rule) continue;
      const ctxVal = contextValues[rule.from];
      if (ctxVal === undefined) continue;
      const target =
        rule.equals !== undefined
          ? rule.equals
          : rule.trueWhenEquals !== undefined
            ? rule.trueWhenEquals
            : true;
      next[check.id] = ctxVal === target ? "yes" : "no";
      prefilled.add(check.id);
    }
    setExclusionAnswers(next);
    setPrefilledExclusionIds(prefilled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, selectedPgdId]);

  // Rebuild supply details when capturedWeightKg updates and we're already on
  // a supply outcome — covers the case where the pre-loop build was deferred
  // because the weight state hadn't propagated yet.
  useEffect(() => {
    if (!selectedPgdId || !pathway) return;
    const outcome = pathway.outcomes[currentNodeId];
    if (outcome?.outcomeType !== "supply") return;
    try {
      const pgd = getPgd(selectedPgdId);
      if (resolveDoseBand(pgd, effectivePatient, activeDoseStepId) === null) return;
      const supplyDetails = buildSupplyDetailsFromPgd(selectedPgdId, effectivePatient, undefined, false, activeDoseStepId);
      setAudit((prev) => recordSupplyDetails(prev, supplyDetails));
    } catch {
      // Still not resolvable — will surface as missing prescription details
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedWeightKg, currentNodeId, selectedPgdId]);

  if (!pathway) {
    return (
      <div className="p-4 space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load pathway. Please try again.</AlertDescription>
        </Alert>
        {onCancel && (
          <Button onClick={onCancel} variant="outline" className="w-full">
            Back
          </Button>
        )}
      </div>
    );
  }

  const isAutoAdvanceType = (n: any) => {
    if (!n) return false;
    if (n.type === "flag") return n.autoAdvance !== false;
    if (n.type === "condition_route") return true;
    if (n.type === "drug_selection") return !isInteractiveDrugSelection(n);
    if (n.type === "weight_capture") return weightCaptureSatisfied();
    if (n.type === "yes_no" && evalAutoAnswer(n.autoAnswer, patient)) {
      return true;
    }
    if (n.type === "drug_elimination" && evalAutoAnswer(n.autoAnswer, patient)) {
      return true;
    }
    return false;
  };
  const answeredVisibleCount = Array.from(answers.keys()).filter((id) => {
    const n = pathway.nodes.find((x) => x.id === id);
    return n ? !isAutoAdvanceType(n) : true;
  }).length;
  const answeredNodesCount = answeredVisibleCount;

  const currentNode =
    pathway.nodes.find((n) => n.id === currentNodeId) ||
    pathway.outcomes[currentNodeId] ||
    null;
  const isOutcome = currentNode?.type === "outcome";
  const outcomeNode = isOutcome
    ? pathway.outcomes[currentNodeId] || currentNode
    : null;

  // Resolve pathway-level clinical links to show in the header (NICE CKS etc.)
  const headerLinks = React.useMemo(() => {
    if (!pathway) return [] as any[];
    const linksMap = (pathway.links as any) || {};
    const refs: string[] = (pathway as any).clinicalNoteLinkRefs || [];
    const resolved: any[] = [];
    // Prefer explicit clinicalNoteLinkRefs when provided
    for (const r of refs) {
      const l = linksMap[r];
      if (l) resolved.push(l);
    }
    // Fallback: gather node-level clinicalLinks
    if (resolved.length === 0) {
      const seen = new Set<string>();
      for (const n of pathway.nodes) {
        const cls = (n as any).clinicalLinks;
        if (Array.isArray(cls)) {
          for (const cl of cls) {
            if (!seen.has(cl.url)) {
              seen.add(cl.url);
              resolved.push(cl);
            }
          }
        }
      }
    }
    return resolved;
  }, [pathway]);

  const getRoutingTarget = (target: string | OutcomeRef): string => {
    if (typeof target === "string") return target;
    return target.outcomeId;
  };

  /** Apply outcomeOverrides — substitutes a supply outcome with another id. */
  const applyOutcomeOverride = (incomingOutcomeId: string): string => {
    const incoming = pathway.outcomes[incomingOutcomeId];
    if (!incoming || incoming.outcomeType !== "supply") return incomingOutcomeId;
    const overrides = pathway.outcomeOverrides || [];
    for (const ov of overrides) {
      const matches = Object.entries(ov.when).every(
        ([k, v]) => (contextValues[k] as any) === v,
      );
      if (matches) return ov.outcomeId;
    }
    return incomingOutcomeId;
  };

  const isWeightCaptureSatisfiedForPgd = (pgdId: string | null): boolean => {
    if (!pgdId) return true;
    try {
      const pgd = getPgd(pgdId);
      if (!pgd.requiresWeightInput) return true;
      const age = effectivePatient.ageYears;
      if (age === undefined || age === null || age < pgd.requiresWeightInput.min || age > pgd.requiresWeightInput.max) return true;
      return typeof effectivePatient.weightKg === "number";
    } catch {
      return true;
    }
  };

  const advanceTo = (nextId: string, pgdIdOverride?: string, pendingContext?: Record<string, unknown>) => {
    let target = nextId;
    // If the engine is about to enter a supply outcome, see if an override applies
    const out = pathway.outcomes[target];
    if (out && out.outcomeType === "supply") {
      target = applyOutcomeOverride(target);
    }
    // If the target is a supply outcome and we have a selected PGD,
    // populate supply details NOW so the OutcomeNode can display the drug name.
    // Only attempt if a dose band can actually be resolved — weight may not yet
    // be reflected in effectivePatient if setCapturedWeightKg just fired.
    const targetOutcome = pathway.outcomes[target];
    const effectivePgdId = pgdIdOverride || selectedPgdId;
    if (targetOutcome?.outcomeType === "supply" && effectivePgdId) {
      try {
        const pgdForCheck = getPgd(effectivePgdId);
        if (resolveDoseBand(pgdForCheck, effectivePatient, activeDoseStepId) !== null) {
          const supplyDetails = buildSupplyDetailsFromPgd(effectivePgdId, effectivePatient, undefined, false, activeDoseStepId);
          setAudit((prev) => recordSupplyDetails(prev, supplyDetails));
        }
      } catch {
        // Silently skip — supply details will be built when outcome renders with updated patient data
      }
    }

    // Skip nodes that would auto-answer to prevent render flash
    let resolvedPgdId: string | null = pgdIdOverride || selectedPgdId || null;
    // Accumulates context values set by storesTo fields during skip — merged into resolvedContext for drug matching
    let accumulatedContext: Record<string, unknown> = pendingContext ? { ...pendingContext } : {};
    while (target && !pathway.outcomes[target]) {
      const node = pathway.nodes.find((n) => n.id === target);
      if (!node) break;

      if (node.type === "yes_no") {
        const n = node as any;
        if (evalAutoAnswer(n.autoAnswer, effectivePatient)) {
          const autoAnswer = n.autoAnswer.value ? "yes" : "no";
          setAudit((prev) => recordAnswer(prev, target, "yes_no", autoAnswer));
          if (n.storesTo) {
            const val = n.autoAnswer.value as boolean;
            setContextValues((p) => ({ ...p, [n.storesTo]: val }));
            accumulatedContext = { ...accumulatedContext, [n.storesTo]: val };
          }
          target = autoAnswer === "yes" ? getRoutingTarget(n.yes) : getRoutingTarget(n.no);
        } else {
          break;
        }
      } else if (node.type === "condition_route") {
        const n = node as any;
        const ctx: ConditionRouteContext = {
          patient: effectivePatient,
          context: { ...contextValues, ...accumulatedContext },
          eligibleDrugIds,
        };
        const resolved = resolveConditionRoute(n, ctx);
        if (n.storesTo && n.storedValue) {
          const val = n.storedValue(ctx);
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          accumulatedContext = { ...accumulatedContext, [n.storesTo]: val };
        }
        target = getRoutingTarget(resolved);
      } else if (node.type === "drug_selection" && !isInteractiveDrugSelection(node)) {
        const n = node as any;
        let matched = false;
        const resolvedContext = { ...contextValues, ...accumulatedContext };
        for (let i = 0; i < n.rules.length; i++) {
          const rule = n.rules[i];
          if (matchesDrugSelectionRule(rule, effectivePatient, resolvedContext)) {
            if (rule.cannotSupplyOutcomeId) {
              target = getRoutingTarget({ outcomeId: rule.cannotSupplyOutcomeId });
            } else {
              const pgdId = rule.pgdId;
              resolvedPgdId = pgdId;
              setSelectedPgdId(pgdId);
              setAudit((prev) => {
                const pgd = getPgd(pgdId);
                return recordDrugSelection(prev, pgdId, pgd.version, i);
              });
              target = n.next;
            }
            matched = true;
            break;
          }
        }
        if (!matched) break;
      } else if (node.type === "weight_capture" && isWeightCaptureSatisfiedForPgd(resolvedPgdId)) {
        const n = node as any;
        target = getRoutingTarget(n.next);
      } else if (node.type === "drug_elimination" && evalAutoAnswer((node as any).autoAnswer, effectivePatient)) {
        const n = node as any;
        const autoVal = n.autoAnswer.value as boolean;
        const { eliminatedDrugIds, logEntries } = computeEliminationAnswer({
          node: n,
          answer: autoVal,
          eligibleDrugIds,
          patient: effectivePatient,
          context: { ...contextValues, ...accumulatedContext },
        });
        const stepResult = resolveEliminationStep(
          n,
          { answer: autoVal, eliminatedDrugIds, logEntries },
          eligibleDrugIds,
          target,
          new Date().toISOString(),
        );
        setEligibleDrugIds(stepResult.remainingDrugIds);
        setEliminationLog((prev) => [...prev, ...stepResult.logEntries]);
        setAudit((prev) => ({
          ...prev,
          eliminationLog: [...(prev.eliminationLog ?? []), ...stepResult.logEntries],
        }));
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: autoVal }));
          accumulatedContext = { ...accumulatedContext, [n.storesTo]: autoVal };
        }
        target = getRoutingTarget(stepResult.nextTarget);
      } else {
        break;
      }
    }

    setHistory((h) => [...h, currentNodeId]);
    setCurrentNodeId(target);
  };

  const handleAnswer = (answer: unknown) => {
    if (!currentNode) return;

    const newAnswers = new Map(answers);
    newAnswers.set(currentNodeId, answer);
    setAnswers(newAnswers);

    setAudit((prev) =>
      recordAnswer(prev, currentNodeId, currentNode.type, answer)
    );

    let nextNodeId: string | null = null;

    switch (currentNode.type) {
      case "yes_no": {
        const n = currentNode as any;
        let pendingCtx: Record<string, unknown> | undefined;
        if (n.storesTo) {
          const val = answer === "yes";
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          pendingCtx = { [n.storesTo]: val };
        }
        const target = answer === "yes" ? n.yes : n.no;
        nextNodeId = getRoutingTarget(target);
        if (nextNodeId && pendingCtx) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "checklist": {
        const n = currentNode as any;
        const selectedIds = answer as string[];
        const noneSelected = selectedIds.length === 0;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: !noneSelected }));
        }
        const target = noneSelected ? n.noneSelected : n.anySelected;
        if (!target) {
          setErrorMessage(`Checklist node ${currentNodeId} has no routing for ${noneSelected ? "noneSelected" : "anySelected"}`);
          return;
        }
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "single_select": {
        const n = currentNode as any;
        const selectedId = answer as string;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: selectedId }));
        }
        const target = n.routing[selectedId];
        if (!target) {
          setErrorMessage(`No routing for option ${selectedId}`);
          return;
        }
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "multi_select": {
        const n = currentNode as any;
        const selectedIds = answer as string[];
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: selectedIds }));
        }
        if (typeof n.minRequired === "number" && (n.thresholdMet || n.thresholdNotMet)) {
          const target = selectedIds.length >= n.minRequired ? n.thresholdMet : n.thresholdNotMet;
          nextNodeId = getRoutingTarget(target);
        } else if (typeof n.routing === "function") {
          const target = n.routing(selectedIds, patient);
          nextNodeId = getRoutingTarget(target);
        } else {
          setErrorMessage("Multi-select node missing routing");
          return;
        }
        break;
      }

      case "severity": {
        const n = currentNode as any;
        const severity = answer as string;
        let target = n[severity];
        if (target && typeof target === "object" && !target.outcomeId) {
          target = contextValues.isFollowUp ? target.followup : target.initial;
        }
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "score": {
        const n = currentNode as any;
        const scoreAnswer = answer as { totalScore: number; selectedIds: string[] };
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: scoreAnswer.totalScore }));
        }
        if (typeof n.routing === "function") {
          const target = n.routing(scoreAnswer.totalScore, patient, contextValues);
          nextNodeId = getRoutingTarget(target);
        } else {
          const scoreKey =
            scoreAnswer.totalScore <= 1
              ? "score_0_or_1"
              : scoreAnswer.totalScore <= 3
                ? "score_2_or_3"
                : "score_4_or_5";
          let target = n.routing?.[scoreKey];
          if (target && typeof target === "object" && !target.outcomeId) {
            target = contextValues.isFollowUp ? target.followup : target.initial;
          }
          nextNodeId = getRoutingTarget(target);
        }
        break;
      }

      case "drug_selection": {
        const n = currentNode as any;
        // Auto variant
        if (!isInteractiveDrugSelection(n)) {
          for (let i = 0; i < n.rules.length; i++) {
            const rule = n.rules[i];
            if (matchesDrugSelectionRule(rule, patient, contextValues)) {
              if (rule.cannotSupplyOutcomeId) {
                const outcomeRef = { outcomeId: rule.cannotSupplyOutcomeId };
                nextNodeId = getRoutingTarget(outcomeRef);
                break;
              }
              if (!rule.pgdId) {
                setErrorMessage("No matching PGD found for this patient");
                return;
              }
              setSelectedPgdId(rule.pgdId);
              setAudit((prev) => {
                const pgd = getPgd(rule.pgdId);
                return recordDrugSelection(prev, rule.pgdId, pgd.version, i);
              });
              nextNodeId = n.next;
              break;
            }
          }
          if (!nextNodeId) {
            setErrorMessage("No matching PGD found for this patient");
            return;
          }
        } else {
          // Interactive — `answer` is { pgdId, ruleIndex, overrideReason?, recommendedPgdId? }
          const a = answer as any;
          setSelectedPgdId(a.pgdId);
          setAudit((prev) => {
            const pgd = getPgd(a.pgdId);
            return recordDrugSelection(prev, a.pgdId, pgd.version, a.ruleIndex, {
              overridden: !!a.overrideReason,
              overrideReason: a.overrideReason,
              recommendedPgdId: a.recommendedPgdId,
            });
          });
          nextNodeId = n.next;
        }
        break;
      }

      case "pgd_exclusion_check": {
        const n = currentNode as any;
        const pgdId = getPgdExclusionCheckPgdId(n, selectedPgdId);
        if (!pgdId) {
          setErrorMessage("No PGD selected");
          return;
        }
        const anyExclusionTriggered =
          n.variant !== "sequential"
            ? (answer as string[]).length > 0
            : Object.values(answer as Record<string, "yes" | "no">).some((v) => v === "yes");
        let activePgdId = pgdId;
        if (shouldSelectOverridePgd(n, anyExclusionTriggered)) {
          setSelectedPgdId(n.pgdIdOverride);
          activePgdId = n.pgdIdOverride;
        }
        nextNodeId = getRoutingTarget(
          anyExclusionTriggered ? n.anyTriggered : n.allCleared,
        );
        if (nextNodeId) {
          advanceTo(nextNodeId, activePgdId);
          return;
        }
        break;
      }

      case "flag": {
        const n = currentNode as any;
        if (n.auditFlags) {
          const flags: string[] = Array.isArray(n.auditFlags) ? n.auditFlags : [n.auditFlags];
          setAccumulatedFlags((prev) => [...prev, ...flags]);
          if (n.autoAdvance === false) {
            setAudit((prev) => ({
              ...prev,
              flagAcknowledgements: [
                ...(prev.flagAcknowledgements ?? []),
                ...flags.map((f: string) => ({
                  nodeId: currentNodeId,
                  flagKey: f,
                  acknowledgedAt: new Date().toISOString(),
                })),
              ],
            }));
          }
        }
        nextNodeId = n.next;
        break;
      }

      case "condition_route": {
        const n = currentNode as any;
        const ctx = buildConditionRouteCtx();
        const resolved = resolveConditionRoute(n, ctx);
        if (n.storesTo && n.storedValue) {
          const val = n.storedValue(ctx);
          setContextValues((p) => ({ ...p, [n.storesTo]: val }));
          if (n.linkedVitalType && onSaveVitals && val != null) {
            onSaveVitals({ type: n.linkedVitalType, data: { value: String(val) }, timestamp: new Date().toISOString() });
          }
        }
        nextNodeId = getRoutingTarget(resolved);
        break;
      }

      case "weight_capture": {
        const n = currentNode as any;
        if (typeof answer === "number" && Number.isFinite(answer) && answer > 0) {
          setCapturedWeightKg(answer);
          if (onSaveVitals) {
            onSaveVitals({ type: "weight", data: { value: String(answer), unit: "kg" }, timestamp: new Date().toISOString() });
          }
        }
        nextNodeId = n.next;
        break;
      }

      case "drug_elimination": {
        const n = currentNode as any;
        const a = answer as DrugEliminationAnswer;
        const stepResult = resolveEliminationStep(
          n,
          {
            answer: a.answer,
            eliminatedDrugIds: a.eliminatedDrugIds,
            logEntries: a.logEntries,
          },
          eligibleDrugIds,
          currentNodeId,
          new Date().toISOString(),
        );
        setEligibleDrugIds(stepResult.remainingDrugIds);
        setEliminationLog((prev) => [...prev, ...stepResult.logEntries]);
        setAudit((prev) => ({
          ...prev,
          eliminationLog: [...(prev.eliminationLog ?? []), ...stepResult.logEntries],
        }));
        if (a.managementPlan) {
          setManagementPlanCaptures((prev) => [
            ...prev,
            {
              nodeId: currentNodeId,
              planText: a.managementPlan!.planText,
              patientAcknowledged: a.managementPlan!.patientAcknowledged,
              capturedAt: new Date().toISOString(),
            },
          ]);
          setAudit((prev) => ({
            ...prev,
            managementPlanCaptures: [
              ...(prev.managementPlanCaptures ?? []),
              {
                nodeId: currentNodeId,
                planText: a.managementPlan!.planText,
                patientAcknowledged: a.managementPlan!.patientAcknowledged,
                capturedAt: new Date().toISOString(),
              },
            ],
          }));
        }
        nextNodeId = getRoutingTarget(stepResult.nextTarget);
        break;
      }

      case "drug_resolution": {
        const n = currentNode as any;
        const a = answer as DrugResolutionAnswer;
        setResolvedDrugId(a.resolvedDrugId);
        setAudit((prev) => ({
          ...prev,
          drugResolution: {
            nodeId: currentNodeId,
            resolvedDrugId: a.resolvedDrugId,
            rankedEligibleDrugs: a.rankedEligibleDrugs,
            selectionReason: a.selectionReason,
            resolvedAt: new Date().toISOString(),
          },
          finalEligibleSet: eligibleDrugIds,
        }));
        if (eligibleDrugIds.length === 0) {
          nextNodeId = getRoutingTarget(n.noDrugsRemaining);
        } else if (eligibleDrugIds.length === 1) {
          nextNodeId = getRoutingTarget(n.singleDrugNext);
        } else {
          nextNodeId = getRoutingTarget(n.multipleDrugsNext);
        }
        break;
      }

      case "numeric_input": {
        const n = currentNode as any;
        const a = answer as NumericInputAnswer;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: a.value }));
        }
        if (n.linkedVitalType && onSaveVitals) {
          const ts = new Date().toISOString();
          if (n.linkedVitalType === "blood-pressure") {
            const systolic = contextValues["bpSystolic"];
            if (systolic != null) {
              onSaveVitals({ type: "blood-pressure", data: { systolic: String(systolic), diastolic: String(a.value) }, timestamp: ts });
            }
          } else {
            onSaveVitals({ type: n.linkedVitalType, data: { value: String(a.value), unit: n.unit }, timestamp: ts });
          }
        }
        if (a.plausibilityConfirmation) {
          setAudit((prev) => ({
            ...prev,
            plausibilityConfirmations: [
              ...(prev.plausibilityConfirmations ?? []),
              {
                nodeId: currentNodeId,
                value: a.value,
                message: a.plausibilityConfirmation!.message,
                confirmedByPharmacist: true,
                confirmedAt: new Date().toISOString(),
              },
            ],
          }));
        }
        nextNodeId = n.next;
        break;
      }

      case "clinician_assessment": {
        const n = currentNode as any;
        const a = answer as ClinicianAssessmentAnswer;
        setAudit((prev) => ({
          ...prev,
          assessmentRecords: [
            ...(prev.assessmentRecords ?? []),
            {
              nodeId: currentNodeId,
              notes: a.notes,
              conclusion: a.conclusion,
              conclusionLabel: a.conclusionLabel,
              recordedAt: new Date().toISOString(),
            },
          ],
        }));
        const target = a.conclusion === "optionA" ? n.conclusions.optionA.next : n.conclusions.optionB.next;
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "attestation": {
        const n = currentNode as any;
        const a = answer as AttestationAnswer;
        setAudit((prev) => ({
          ...prev,
          attestationRecords: [
            ...(prev.attestationRecords ?? []),
            {
              nodeId: currentNodeId,
              attested: a.attested,
              notes: a.notes,
              conclusion: a.conclusion,
              conclusionLabel: a.conclusionLabel,
              attestedAt: new Date().toISOString(),
            },
          ],
        }));
        const target = a.conclusion === "optionA" ? n.conclusions.optionA.next : n.conclusions.optionB.next;
        nextNodeId = getRoutingTarget(target);
        break;
      }

      case "iotf_lookup": {
        const n = currentNode as any;
        const a = answer as IOTFLookupAnswer;
        if (n.storesTo) {
          setContextValues((p) => ({ ...p, [n.storesTo]: a.result }));
        }
        const routing = n.resultRouting?.[a.result];
        if (routing) {
          nextNodeId = getRoutingTarget(routing);
        } else {
          nextNodeId = n.next;
        }
        break;
      }

      case "confirmation_card": {
        const n = currentNode as any;
        const a = answer as ConfirmationCardResult;
        if (a.amendments.length > 0) {
          setAudit((prev) => ({
            ...prev,
            confirmationCardAmendments: [
              ...(prev.confirmationCardAmendments ?? []),
              ...a.amendments,
            ],
          }));
        }
        if (a.cannotProceedOutcomeId) {
          nextNodeId = getRoutingTarget(a.cannotProceedOutcomeId);
        } else {
          nextNodeId = n.next;
        }
        break;
      }

      case "bmi_capture": {
        const n = currentNode as any;
        const a = answer as BMICaptureAnswer;
        const pendingCtx: Record<string, unknown> = {};
        if (n.storesToHeight) {
          setContextValues((p) => ({ ...p, [n.storesToHeight]: a.heightCm }));
          pendingCtx[n.storesToHeight] = a.heightCm;
        }
        if (n.storesToWeight) {
          setContextValues((p) => ({ ...p, [n.storesToWeight]: a.weightKg }));
          setCapturedWeightKg(a.weightKg);
          pendingCtx[n.storesToWeight] = a.weightKg;
        }
        if (n.storesToBmi) {
          setContextValues((p) => ({ ...p, [n.storesToBmi]: a.bmi }));
          pendingCtx[n.storesToBmi] = a.bmi;
        }
        if (onSaveVitals) {
          const ts = new Date().toISOString();
          onSaveVitals({ type: "bmi", data: { value: String(a.bmi), unit: "kg/m²", heightCm: a.heightCm, weightKg: a.weightKg }, timestamp: ts });
        }
        nextNodeId = getRoutingTarget(n.next);
        if (nextNodeId) {
          advanceTo(nextNodeId, undefined, pendingCtx);
          return;
        }
        break;
      }

      case "gateway_alarm":
      case "gateway_progression": {
        const n = currentNode as any;
        // Gateway passage is recorded by the dedicated useEffect on currentNodeId —
        // do NOT call recordGateway here again or it double-records.
        if (currentNode.type === "gateway_alarm" && n.action === "close") {
          // Close the consultation immediately — record an emergency outcome
          // so the summary / completion modal can describe what happened, then
          // surface the summary for review.
          setAudit((prev) =>
            recordOutcome(
              prev,
              currentNodeId,
              "emergency",
              n.body,
              n.heading,
            ),
          );
          setEngineState("summary");
          return;
        }
        nextNodeId =
          currentNode.type === "gateway_progression" ? n.next : null;
        break;
      }

      case "outcome": {
        setAudit((prev) => {
          let updated = recordOutcome(
            prev,
            currentNodeId,
            outcomeNode?.outcomeType || "refer_routine",
            (outcomeNode as any)?.reason,
            (outcomeNode as any)?.title,
            (outcomeNode as any)?.safetyNetting,
          );
          // Record supply details immediately when outcome is supply
          if (outcomeNode?.outcomeType === "supply" && selectedPgdId) {
            try {
              const pgdForSupply = getPgd(selectedPgdId);
              const band = resolveDoseBand(pgdForSupply, effectivePatient, activeDoseStepId);
              if (band) {
                const supplyDetails = buildSupplyDetailsFromPgd(
                  selectedPgdId,
                  effectivePatient,
                  undefined,
                  false,
                  activeDoseStepId,
                );
                updated = recordSupplyDetails(updated, supplyDetails);
              } else {
                setErrorMessage(
                  "Supply details could not be recorded — patient weight may not be captured. Please review the prescription manually.",
                );
              }
            } catch (e) {
              console.error("Failed to build supply details:", e);
              setErrorMessage(
                "Supply details could not be recorded. Please review the prescription manually.",
              );
            }
          }
          return updated;
        });
        if (outcomeNode?.outcomeType === "chain" && (outcomeNode as any).chainId) {
          const chainId = (outcomeNode as any).chainId;
          setAudit((prev) => {
            const updated = {
              ...prev,
              finalOutcome: prev.finalOutcome
                ? { ...prev.finalOutcome, chainId }
                : undefined,
              pathwaySections: [
                ...(prev.pathwaySections ?? []),
                {
                  pathwayId,
                  sectionLabel: pathway.name,
                  completedAt: new Date().toISOString(),
                },
              ],
            };
            setTimeout(() => {
              setEngineState("complete");
              onComplete({ ...updated, contextSnapshot: contextValues });
            }, 0);
            return updated;
          });
          return;
        } else if (outcomeNode?.outcomeType === "supply" && selectedPgdId) {
          setEngineState("prescribing");
        } else {
          setEngineState("summary");
        }
        return;
      }
    }

    if (nextNodeId) {
      const chainOutcome = pathway.outcomes[nextNodeId];
      if (chainOutcome?.outcomeType === "chain" && (chainOutcome as any).chainId) {
        const chainId = (chainOutcome as any).chainId;
        setAudit((prev) => {
          const withOutcome = recordOutcome(
            prev,
            nextNodeId,
            "chain",
            (chainOutcome as any)?.reason,
            (chainOutcome as any)?.title,
          );
          const updated = {
            ...withOutcome,
            finalOutcome: { ...withOutcome.finalOutcome!, chainId },
            pathwaySections: [
              ...(withOutcome.pathwaySections ?? []),
              {
                pathwayId,
                sectionLabel: pathway.name,
                completedAt: new Date().toISOString(),
              },
            ],
          };
          setTimeout(() => {
            setEngineState("complete");
            onComplete({ ...updated, contextSnapshot: contextValues });
          }, 0);
          return updated;
        });
        return;
      }
      advanceTo(nextNodeId);
    }
  };

  const handleDirectNavigate = useCallback(
    (nodeId: string) => {
      setHistory((h) => [...h, currentNodeId]);
      setCurrentNodeId(nodeId);
    },
    [currentNodeId],
  );

  const calculateAgeYearsFromDateOfBirth = (dateOfBirth: unknown): number | null => {
    if (typeof dateOfBirth !== "string" || !dateOfBirth.trim()) return null;
    const trimmed = dateOfBirth.trim();
    const ukMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const dob = ukMatch
      ? new Date(Number(ukMatch[3]), Number(ukMatch[2]) - 1, Number(ukMatch[1]))
      : new Date(trimmed);
    if (Number.isNaN(dob.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return Math.max(0, age);
  };

  const handleBannerConfirm = (
    amendments: Amendment[],
    extras: { consentObtained: boolean },
  ) => {
    if (!pathway) {
      console.error("Pathway not loaded");
      return;
    }


    const confirmedValues: Record<string, unknown> = {};
    pathway.demographicConfiguration.bannerFields.forEach((field) => {
      const amendment = amendments.find((a) => a.field === field.field);
      confirmedValues[field.field] = amendment ? amendment.newValue : (patient as any)[field.field];
    });

    // Check demographic eligibility rules if defined
    if (pathway.demographicEligibility) {
      const elig = pathway.demographicEligibility;

      // Sex check
      if (elig.requiresSex && elig.requiresSex !== "any") {
        const isFemale = confirmedValues.isFemale;
        if (
          (elig.requiresSex === "female" && isFemale === false) ||
          (elig.requiresSex === "male" && isFemale === true)
        ) {
          setDemographicBlockMessage(
            elig.blockMessage || "This patient does not meet the sex requirement for this pathway.",
          );
          return;
        }
      }

      // Age check
      const ageYears = calculateAgeYearsFromDateOfBirth(confirmedValues.dateOfBirth);
      if (ageYears !== null) {
        if (elig.minAge !== undefined && ageYears < elig.minAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires a minimum age of ${elig.minAge} years.`,
          );
          return;
        }
        if (elig.maxAge !== undefined && ageYears >= elig.maxAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires an age under ${elig.maxAge} years.`,
          );
          return;
        }
      }
    }

    setDemographicBlockMessage(null);
    setAudit((prev) =>
      recordBannerConfirmation(prev, confirmedValues, amendments, {
        consentObtained: extras.consentObtained,
      }),
    );

    const demographicFields = new Set(["dateOfBirth", "biologicalSex", "isFemale", "ethnicAdjustmentGroup"]);
    const demographicAmendments = amendments.filter((a) => demographicFields.has(a.field));
    if (demographicAmendments.length > 0 && onDemographicAmended) {
      onDemographicAmended(demographicAmendments);
    }

    setContextValues((p) => ({
      ...p,
      consentObtained: extras.consentObtained,
    }));
    setEngineState("consulting");
  };

  if (engineState === "banner") {
    // Check eligibility on initial render
    if (!demographicBlockMessage && pathway?.demographicEligibility) {
      const elig = pathway.demographicEligibility;
      const ageYears = calculateAgeYearsFromDateOfBirth(patient.dateOfBirth);

      // Sex check
      if (elig.requiresSex && elig.requiresSex !== "any") {
        if (
          (elig.requiresSex === "female" && patient.isFemale === false) ||
          (elig.requiresSex === "male" && patient.isFemale === true)
        ) {
          setDemographicBlockMessage(
            elig.blockMessage || "This patient does not meet the sex requirement for this pathway.",
          );
        }
      }

      // Age check
      if (!demographicBlockMessage && ageYears !== null) {
        if (elig.minAge !== undefined && ageYears < elig.minAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires a minimum age of ${elig.minAge} years.`,
          );
        } else if (elig.maxAge !== undefined && ageYears >= elig.maxAge) {
          setDemographicBlockMessage(
            elig.blockMessage || `This pathway requires an age under ${elig.maxAge} years.`,
          );
        }
      }
    }

    return (
      <div
        className={`flex flex-col h-full ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
          }`}
      >
        <div
          className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
            } px-4 pt-2 pb-3`}
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p
                className={`text-[10px] uppercase tracking-wide font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
              >
                Pathway
              </p>
              <h2
                className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                title={pathway.name}
              >
                {pathway.name}
              </h2>
              {headerLinks.length > 0 && (
                <Collapsible open={clinicalRefsOpen} onOpenChange={setClinicalRefsOpen} className="mt-1.5">
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      title={clinicalRefsOpen ? "Collapse clinical references" : "Expand clinical references"}
                      className="text-xs h-auto py-1"
                    >
                      <Info className="h-3 w-3" />
                      <span>Clinical References ({headerLinks.length})</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${clinicalRefsOpen ? "rotate-180" : ""}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="flex flex-wrap gap-1.5">
                      {headerLinks.map((l: any, idx: number) => (
                        <a
                          key={idx}
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Open ${l.label} in a new tab`}
                          style={{ animation: clinicalRefsOpen ? `fadeInSlideUp 300ms ease-out ${idx * 10}ms both` : 'none' }}
                          className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${isDarkMode ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600" : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"}`}
                        >
                          <Info className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                          <span className="underline underline-offset-2 decoration-dotted">{l.label}</span>
                          <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                        </a>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {demographicBlockMessage && (
            <OutcomeNode
              node={{
                id: "OUT_INELIGIBLE",
                type: "outcome",
                outcomeType: "refer_routine",
                title: "Refer to prescriber",
                reason: demographicBlockMessage,
                safetyNetting: ["Patient is not eligible for this pathway. Refer to a prescriber for assessment and treatment."],
              } as any}
              isDarkMode={isDarkMode}
              pathway={pathway}
              onClose={() => {
                setAudit((prev) =>
                  recordOutcome(
                    prev,
                    "OUT_INELIGIBLE",
                    "refer_routine",
                    demographicBlockMessage,
                    "Refer to prescriber",
                    ["Patient is not eligible for this pathway. Refer to a prescriber for assessment and treatment."]
                  )
                );
                setEngineState("summary");
              }}
            />
          )}
          {!demographicBlockMessage && (
            <DemographicConfirmationBanner
              configuration={pathway.demographicConfiguration}
              patient={patient}
              isDarkMode={isDarkMode}
              onConfirm={handleBannerConfirm}
              onCancel={onCancel}
              onConsentRefused={onConsentRefused}
            />
          )}
        </div>
      </div>
    );
  }

  // Lookup PGD-derived counselling/cautions for supply outcomes (used in
  // both the prescribing and summary render paths below).
  let outcomeCounselling: string[] | undefined;
  let outcomeCautions: any[] | undefined;
  if (
    isOutcome &&
    outcomeNode?.outcomeType === "supply" &&
    selectedPgdId
  ) {
    try {
      const pgd = getPgd(selectedPgdId);
      outcomeCounselling = pgd.counselling;
      outcomeCautions = pgd.cautions;
    } catch {
      // ignore
    }
  }

  if (engineState === "prescribing" && selectedPgdId && outcomeNode?.outcomeType === "supply") {
    let defaultCategory: "pgd" | "pharmacy-first" = "pgd";
    try {
      const pgd = getPgd(selectedPgdId);
      if (pgd.frameworkType === "pharmacy_first") {
        defaultCategory = "pharmacy-first";
      }
    } catch {
      // fall back to "pgd"
    }
    return (
      <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        <div
          className={`border rounded-2xl p-6 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
            }`}
        >
          <h2
            className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"
              }`}
          >
            Generate PGD Supply
          </h2>
          {headerLinks.length > 0 && (
            <div className="mt-1.5 mb-2 flex flex-wrap gap-1.5">
              {headerLinks.map((l: any, idx: number) => (
                <a
                  key={idx}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open ${l.label} in a new tab`}
                  className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${isDarkMode ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600" : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"}`}
                >
                  <Info className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                  <span className="underline underline-offset-2 decoration-dotted">{l.label}</span>
                  <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                </a>
              ))}
            </div>
          )}
          <p
            className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
          >
            The PGD-permitted medication, dose and quantity have been pre-filled
            from {selectedPgdId}. You can edit any field (e.g. swap formulation
            based on stock) before issuing.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEngineState("summary")}
            >
              Skip
            </Button>
            <PrescriptionModal
              trigger={
                <Button size="lg" className="flex-1">
                  Issue prescription
                </Button>
              }
              isDarkMode={isDarkMode}
              prefillItems={prefillItems}
              allowedMedications={allowedMedications}
              defaultCategory={defaultCategory}
              lockedMedications={false}
              onSave={(prescription) => {
                const items: any[] = prescription?.items || [];
                const firstItem = items.find((item: any) => item?.value);
                if (firstItem) {
                  setAudit((prev) => {
                    if (!prev.supplyDetails) return prev;
                    return {
                      ...prev,
                      supplyDetails: {
                        ...prev.supplyDetails,
                        drugId: firstItem.value,
                      },
                    };
                  });
                }
                setEngineState("summary");
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (engineState === "summary") {
    const chainStartedAt = contextValues._chainStartedAt as string | undefined;
    const chainVisibleCount = (contextValues._chainVisibleAnswerCount as number | undefined) ?? 0;
    return (
      <ConsultationSummary
        audit={audit}
        pathway={pathway}
        isDarkMode={isDarkMode}
        overrideStartedAt={chainStartedAt}
        overrideTotalAnswers={chainVisibleCount > 0 ? chainVisibleCount + answeredVisibleCount : answeredVisibleCount}
        onClose={() => {
          setEngineState("complete");
          onComplete({ ...audit, contextSnapshot: { ...contextValues, _visibleAnswerCount: answeredVisibleCount } });
        }}
      />
    );
  }

  if (engineState === "complete") {
    return null;
  }

  if (!currentNode) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Current node not found: {currentNodeId}</AlertDescription>
        </Alert>
      </div>
    );
  }


  return (
    <div
      className={`flex flex-col h-full ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
    >
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
          } px-4 pt-2 pb-3`}
      >
        <div className="flex items-center justify-between mb-1.5">
          <div className="min-w-0">
            <p
              className={`text-[10px] uppercase tracking-wide font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
            >
              Pathway
            </p>
            <h2
              className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"
                }`}
              title={pathway.name}
            >
              {pathway.name}
            </h2>
            {headerLinks.length > 0 && (
              <Collapsible open={clinicalRefsOpen} onOpenChange={setClinicalRefsOpen} className="mt-1.5">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    title={clinicalRefsOpen ? "Collapse clinical references" : "Expand clinical references"}
                    className="text-xs h-auto py-1"
                  >
                    <Info className="h-3 w-3" />
                    <span>Clinical References ({headerLinks.length})</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${clinicalRefsOpen ? "rotate-180" : ""}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <div className="flex flex-wrap gap-1.5">
                    {headerLinks.map((l: any, idx: number) => (
                      <a
                        key={idx}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${l.label} in a new tab`}
                        style={{ animation: clinicalRefsOpen ? `fadeInSlideUp 300ms ease-out ${idx * 10}ms both` : 'none' }}
                        className={`group inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${isDarkMode ? "border-gray-700 bg-gray-900 text-blue-300 hover:bg-gray-800 hover:border-blue-600" : "border-gray-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-300"}`}
                      >
                        <Info className={`h-3 w-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                        <span className="underline underline-offset-2 decoration-dotted">{l.label}</span>
                        <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                      </a>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mb-2">
          {history.length > 0 || onCancel ? (
            <button
              onClick={handleStepBack}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${isDarkMode
                  ? "hover:bg-gray-700 text-gray-400"
                  : "hover:bg-gray-100 text-gray-600"
                }`}
            >
              <ChevronLeft className="h-4 w-4" />
              {history.length === 0 ? "Cancel" : "Back"}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            {audit.paymentTriggered && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Payment claimable
              </span>
            )}
            <p
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
            >
              Question {answeredNodesCount + 1}
            </p>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        </div>
      )}

      <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
        {currentNode.type === "yes_no" && !evalAutoAnswer((currentNode as any).autoAnswer, effectivePatient) && (
          <YesNoNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as "yes" | "no" | null) || null}
            stepNumber={answeredNodesCount + 1}
          />
        )}

        {currentNode.type === "checklist" && (
          <ChecklistNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as string[]) || []}
            stepNumber={answeredNodesCount + 1}
            patient={patient}
          />
        )}

        {currentNode.type === "score" && (
          <ScoreNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as any) || null}
            stepNumber={answeredNodesCount + 1}
            onOpenCalculator={onOpenCalculator}
          />
        )}

        {currentNode.type === "single_select" && (
          <SingleSelectNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as string) || null}
            stepNumber={answeredNodesCount + 1}
          />
        )}

        {currentNode.type === "multi_select" && (
          <MultiSelectNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as string[]) || []}
            stepNumber={answeredNodesCount + 1}
            patient={patient}
          />
        )}

        {currentNode.type === "severity" && (
          <SeverityNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            value={(answers.get(currentNodeId) as "mild" | "moderate" | "severe" | null) || null}
            stepNumber={answeredNodesCount + 1}
          />
        )}

        {currentNode.type === "gateway_alarm" && (
          <GatewayAlarmNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            pathway={pathway}
            onAcknowledge={() => handleAnswer(true)}
            onOpenCalculator={onOpenCalculator}
            onBack={handleStepBack}
          />
        )}

        {currentNode.type === "gateway_progression" && (() => {
          const n = currentNode as any;
          if (n.id === "Q12_blood_pressure_controlled") {
            return (
              <InlineBPCaptureNode
                key={currentNode.id}
                isDarkMode={isDarkMode}
                patientAgeYears={(effectivePatient as any).ageYears}
                onSave={(payload) => { onSaveVitals?.(payload); }}
                onAnswer={(answer) => {
                  const nextNodeId =
                    answer.conclusion === "optionA"
                      ? "Q13_active_suicidal_ideation"
                      : "Q12_bp_mysimba_elimination";
                  handleDirectNavigate(nextNodeId);
                }}
                stepNumber={answeredNodesCount + 1}
                existingSystolic={(() => {
                  const v = patientVitals?.bloodPressure?.systolic?.value;
                  if (v == null) return null;
                  const n = typeof v === "string" ? parseFloat(v) : v;
                  return Number.isFinite(n) && n > 0 ? n : null;
                })()}
                existingDiastolic={(() => {
                  const v = patientVitals?.bloodPressure?.diastolic?.value;
                  if (v == null) return null;
                  const n = typeof v === "string" ? parseFloat(v) : v;
                  return Number.isFinite(n) && n > 0 ? n : null;
                })()}
              />
            );
          }
          return (
            <GatewayProgressionNode
              node={n}
              isDarkMode={isDarkMode}
              onContinue={() => handleAnswer(true)}
            />
          );
        })()}

        {currentNode.type === "bmi_capture" && (
          <BMICaptureNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(value) => handleAnswer(value)}
            stepNumber={answeredNodesCount + 1}
            patientEthnicAdjustmentGroup={(() => {
              const raw = (effectivePatient as any)?.ethnicAdjustmentGroup;
              const val = typeof raw === "object" && raw !== null ? raw.value : raw;
              return val;
            })()}
            existingHeightCm={(() => {
              const v = patientVitals?.height?.value;
              if (v == null) return null;
              const n = typeof v === "string" ? parseFloat(v) : v;
              return Number.isFinite(n) && n > 0 ? n : null;
            })()}
            existingWeightKg={(() => {
              const v = patientVitals?.weight?.value;
              if (v == null) return null;
              const n = typeof v === "string" ? parseFloat(v) : v;
              return Number.isFinite(n) && n > 0 ? n : null;
            })()}
          />
        )}

        {currentNode.type === "weight_capture" && (
          <WeightCaptureNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            onAnswer={(w) => handleAnswer(w)}
            value={(answers.get(currentNodeId) as number) ?? capturedWeightKg ?? null}
            stepNumber={answeredNodesCount + 1}
            pgd={selectedPgdId ? (() => { try { return getPgd(selectedPgdId); } catch { return undefined; } })() : undefined}
          />
        )}

        {currentNode.type === "outcome" && outcomeNode && (
          <OutcomeNode
            node={outcomeNode as any}
            isDarkMode={isDarkMode}
            counsellingPoints={outcomeCounselling}
            cautions={outcomeCautions}
            pathway={pathway}
            supplyDetails={audit.supplyDetails}
            drugId={(outcomeNode as any)?.drugRef || audit.supplyDetails?.drugId}
            onClose={() => handleAnswer(true)}
            onOpenCalculator={onOpenCalculator}
            onCreateTreatmentPlan={
              pathway.id.startsWith("aht-") && (outcomeNode as any)?.outcomeType === "supply"
                ? handleCreateAHTTreatmentPlan
                : undefined
            }
            treatmentPlanExists={ahtTreatmentPlanCreated}
          />
        )}

        {showAHTTreatmentPlanModal &&
          audit.supplyDetails &&
          ahtPlannedAppointments.length > 0 && (() => {
            const drugId = audit.supplyDetails!.drugId as AHTDrugId;
            const pgdId = audit.supplyDetails!.pgdId ?? selectedPgdId ?? "";
            let pgd: ReturnType<typeof getPgd> | undefined;
            try { pgd = getPgd(pgdId); } catch { /* ignore */ }
            const reviewSchedule = pgd?.reviewSchedule;
            if (!reviewSchedule) return null;

            const today = new Date().toISOString().split("T")[0] ?? new Date().toISOString().substring(0, 10);
            const dates = calculateTreatmentPlanDates({
              actualSupplyDate: today,
              initialSupplyDate: today,
              currentWeek: 1,
              reviewSchedule,
            });

            const weightKg =
              typeof contextValues.weightKg === "number" ? contextValues.weightKg : 0;
            const bmi =
              typeof contextValues.bmi === "number" ? contextValues.bmi : 0;

            return (
              <AHTTreatmentPlanConfirmModal
                open={showAHTTreatmentPlanModal}
                isDarkMode={isDarkMode}
                patientName={patientDisplayName ?? patient.patientId}
                patientId={patient.patientId}
                patientEmail={patientEmail ?? null}
                onUpdatePatientEmail={onUpdatePatientEmail}
                episodeOrigin="initialSupply"
                drugName={drugId}
                pgdId={pgdId}
                initialDose={audit.supplyDetails!.instructionText}
                baselineWeightKg={weightKg}
                baselineBmi={bmi}
                plannedAppointments={ahtPlannedAppointments}
                onCancel={() => setShowAHTTreatmentPlanModal(false)}
                onConfirm={async () => {
                  const plan = buildAHTTreatmentPlan({
                    patientId: patient.patientId,
                    patientName: patientDisplayName ?? patient.patientId,
                    consultationId: audit.consultationId,
                    pharmacistName: "",
                    pharmacistRegistrationNumber: "",
                    createdBy: "",
                    episodeOrigin: "initialSupply",
                    resolvedDrugId: drugId,
                    pgdId,
                    pgdVersion: audit.supplyDetails!.pgdVersion,
                    initialDose: audit.supplyDetails!.instructionText,
                    baselineWeightKg: weightKg,
                    baselineWaistCircumferenceCm: null,
                    baselineBmi: bmi,
                    dates,
                    batchNumber: "",
                    expiryDate: "",
                    brandName: null,
                    clinicalRationale: "",
                    plannedAppointments: ahtPlannedAppointments,
                  });
                  await onCreateAHTTreatmentPlan?.(plan);
                  setShowAHTTreatmentPlanModal(false);
                  setAhtTreatmentPlanCreated(true);
                }}
              />
            );
          })()}

        {currentNode.type === "drug_selection" &&
          isInteractiveDrugSelection(currentNode) && (
            <DrugSelectionNode
              node={currentNode as any}
              isDarkMode={isDarkMode}
              context={contextValues}
              pathwayPgdRefs={pathway.pgdRefs}
              stepNumber={answeredNodesCount + 1}
              onAccept={(rule, ruleIndex) =>
                handleAnswer({ pgdId: rule.pgdId, ruleIndex })
              }
              onOverride={(pgdId, overrideReason, recommendedPgdId) =>
                handleAnswer({
                  pgdId,
                  ruleIndex: -1,
                  overrideReason,
                  recommendedPgdId,
                })
              }
              onSelect={(pgdId) => handleAnswer({ pgdId, ruleIndex: 0 })}
            />
          )}

        {currentNode.type === "drug_elimination" && !evalAutoAnswer((currentNode as any).autoAnswer, effectivePatient) && (
          <DrugEliminationNode
            node={currentNode as any}
            eligibleDrugIds={eligibleDrugIds}
            drugLabels={
              (pathway as any).drugLabels ??
              Object.fromEntries(eligibleDrugIds.map((id) => {
                try { return [id, getDrug(id).name]; } catch { return [id, id]; }
              }))
            }
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
            evaluationPatient={effectivePatient}
            evaluationContext={contextValues}
          />
        )}

        {currentNode.type === "drug_resolution" && (
          <DrugResolutionNode
            node={currentNode as any}
            eligibleDrugIds={eligibleDrugIds}
            drugLabels={
              (pathway as any).drugLabels ??
              Object.fromEntries(eligibleDrugIds.map((id) => {
                try { return [id, getDrug(id).name]; } catch { return [id, id]; }
              }))
            }
            accumulatedFlags={accumulatedFlags}
            auditFlagLabels={(pathway as any).auditFlagLabels}
            managementPlanCaptures={managementPlanCaptures}
            preferenceCaptures={preferenceCaptures}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "numeric_input" && (
          <NumericInputNode
            node={currentNode as any}
            prePopulatedValue={
              (currentNode as any).storesTo
                ? (contextValues[(currentNode as any).storesTo] as number | undefined) ?? null
                : null
            }
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "clinician_assessment" && (
          <ClinicianAssessmentNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "attestation" && (
          <AttestationNode
            node={currentNode as any}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "iotf_lookup" && (
          <IOTFLookupNode
            node={currentNode as any}
            ageMonths={
              effectivePatient.ageYears !== undefined && effectivePatient.ageYears !== null
                ? Math.round(effectivePatient.ageYears * 12)
                : 0
            }
            biologicalSex={
              effectivePatient.isFemale === true
                ? "female"
                : effectivePatient.isFemale === false
                  ? "male"
                  : "other"
            }
            bmi={
              (contextValues.bmi as number) ??
              (typeof effectivePatient.weightKg === "number" &&
                typeof (effectivePatient as any).heightCm === "number" &&
                (effectivePatient as any).heightCm > 0
                ? (effectivePatient.weightKg / (((effectivePatient as any).heightCm / 100) ** 2))
                : 0)
            }
            iotfTable={(() => { try { return getIOTFTable((currentNode as any).iotfTableId); } catch { return { id: "", version: "", status: "pending_data" as const, source: "", validFrom: "", cutOffs: [] }; } })()}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "confirmation_card" && (
          <ConfirmationCardNode
            node={currentNode as any}
            auditAnswers={[
              ...(Array.isArray(contextValues._chainAuditAnswers) ? contextValues._chainAuditAnswers as any[] : []),
              ...audit.answers,
            ]}
            isDarkMode={isDarkMode}
            stepNumber={answeredNodesCount + 1}
            onAnswer={(value) => handleAnswer(value)}
          />
        )}

        {currentNode.type === "flag" && (currentNode as any).autoAdvance === false && (
          <div
            className={`border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
              >
                {answeredNodesCount + 1}
              </div>
              <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                {(currentNode as any).text || "Important notice"}
              </h3>
            </div>
            {(currentNode as any).hint && (
              <p className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                {(currentNode as any).hint}
              </p>
            )}
            <Button className="w-full" onClick={() => handleAnswer(null)}>
              Acknowledge &amp; continue
            </Button>
          </div>
        )}

        {((currentNode.type === "drug_selection" &&
          !isInteractiveDrugSelection(currentNode)) ||
          (currentNode.type === "flag" && (currentNode as any).autoAdvance !== false)) && (
            <div className="text-center py-8">
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                Processing...
              </p>
            </div>
          )}

        {currentNode.type === "pgd_exclusion_check" && ((currentNode as any).pgdIdOverride || selectedPgdId) && (() => {
          const n = currentNode as any;
          const pgdId = n.pgdIdOverride || selectedPgdId;
          let pgd;
          try {
            pgd = getPgd(pgdId);
          } catch {
            return (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Could not load PGD {pgdId}.
                </AlertDescription>
              </Alert>
            );
          }
          const checks = pgd.preSupplyChecks || [];

          if (n.variant !== "sequential") {
            const label = String(pgd.id).startsWith("protocol-") ? "Protocol" : "PGD";
            let drugName: string | null = null;
            try { if (pgd.drugRef) drugName = getDrug(pgd.drugRef).name; } catch { drugName = null; }
            const condition = (pgd.indication || "").split(/[—–-]/)[0].trim();
            const parts = [condition, drugName].filter(Boolean).join(" — ");
            const syntheticNode: ChecklistNodeShape = {
              id: currentNodeId,
              type: "checklist",
              text: `${label} pre-supply checks — ${parts} (${pgd.reference})`,
              items: checks.map((check) => ({
                id: check.id,
                text: check.question,
                hint: check.hint,
                linkRefs: check.linkRefs,
              })),
              includeNoneOption: true,
              anySelected: n.anyTriggered,
              noneSelected: n.allCleared,
            };
            return (
              <ChecklistNode
                node={syntheticNode}
                isDarkMode={isDarkMode}
                onAnswer={(selectedIds) => handleAnswer(selectedIds)}
                value={(answers.get(currentNodeId) as string[]) || []}
                stepNumber={answeredNodesCount + 1}
                patient={patient}
              />
            );
          }

          const allAnswered = checks.every(
            (c) => exclusionAnswers[c.id] === "yes" ||
              exclusionAnswers[c.id] === "no",
          );
          const anyYes = checks.some((c) => exclusionAnswers[c.id] === "yes");

          return (
            <div
              className={`border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${isDarkMode ? "bg-white text-black" : "bg-black text-white"
                    }`}
                >
                  {answeredNodesCount + 1}
                </div>
                <h3
                  className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {(() => {
                    const label = String(pgd.id).startsWith("protocol-") ? "Protocol" : "PGD";
                    let drugName: string | null = null;
                    try {
                      if (pgd.drugRef) drugName = getDrug(pgd.drugRef).name;
                    } catch {
                      drugName = null;
                    }
                    const condition = (pgd.indication || "").split(/[—–-]/)[0].trim();
                    const parts = [condition, drugName].filter(Boolean).join(" — ");
                    return `${label} pre-supply checks — ${parts} (${pgd.reference})`;
                  })()}
                </h3>
              </div>
              <p className={`text-xs mb-4 ml-9 ${isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}>
                Answer each — any "Yes" means the patient cannot be supplied under this {String(pgd.id).startsWith("protocol-") ? "protocol" : "PGD"}.
              </p>

              <div className="space-y-2">
                {checks.map((check) => {
                  const prefilled = prefilledExclusionIds.has(check.id);
                  return (
                    <div
                      key={check.id}
                      className={`flex items-center justify-between gap-3 p-3 border rounded-lg ${isDarkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                    >
                      <div className="flex-1">
                        <div>
                          <p className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"
                            }`}>
                            {check.question}
                            {check.hint && (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="ml-1 inline-flex align-[-2px]"
                                      aria-label={check.hint}
                                    >
                                      <CircleHelp
                                        className={`h-3.5 w-3.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"
                                          }`}
                                      />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    align="center"
                                    collisionPadding={16}
                                    className="max-w-72 text-xs leading-relaxed"
                                  >
                                    {check.hint}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </p>
                        </div>
                        {check.linkedVitalType && onSaveVitals && (
                          <VitalsModal
                            vitalType={check.linkedVitalType as any}
                            onSave={onSaveVitals}
                            isDarkMode={isDarkMode}
                            trigger={
                              <button
                                type="button"
                                className={`mt-1 inline-flex items-center gap-1 text-xs underline ${isDarkMode ? "text-blue-300" : "text-blue-700"
                                  }`}
                              >
                                <Thermometer className="h-3 w-3" />
                                Add {check.linkedVitalType} reading
                              </button>
                            }
                          />
                        )}
                        {prefilled && (
                          <span
                            className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full border ${isDarkMode
                                ? "border-blue-700 text-blue-300 bg-blue-950/40"
                                : "border-blue-300 text-blue-800 bg-blue-50"
                              }`}
                          >
                            Answered earlier
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {(["yes", "no"] as const).map((opt) => {
                          const selected = exclusionAnswers[check.id] === opt;
                          const baseCls =
                            "h-9 w-14 inline-flex items-center justify-center rounded-md border text-sm font-medium transition select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";
                          let stateCls: string;
                          if (selected) {
                            if (opt === "yes") {
                              // Red for Yes
                              stateCls = isDarkMode
                                ? "border-red-600 bg-red-950 text-red-100"
                                : "border-red-300 bg-red-50 text-red-900";
                            } else {
                              // Green for No
                              stateCls = isDarkMode
                                ? "border-green-600 bg-green-950 text-green-100"
                                : "border-green-300 bg-green-50 text-green-900";
                            }
                          } else {
                            stateCls = isDarkMode
                              ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                              : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50";
                          }
                          return (
                            <button
                              key={`${check.id}-${opt}`}
                              type="button"
                              aria-pressed={selected}
                              onClick={(e) => {
                                setExclusionAnswers((p) => ({
                                  ...p,
                                  [check.id]: opt,
                                }));
                                (e.currentTarget as HTMLButtonElement).blur();
                              }}
                              className={`${baseCls} ${stateCls}`}
                            >
                              {opt === "yes" ? "Yes" : "No"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {anyYes && (
                <Alert className="mt-4 border-amber-200 bg-amber-50 text-amber-900">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    One or more exclusions triggered — patient cannot be supplied under this PGD.
                  </AlertDescription>
                </Alert>
              )}

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => handleAnswer(exclusionAnswers)}
                  disabled={!allAnswered}
                >
                  Continue
                </Button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
